/* istanbul ignore file */
/* cspell:ignore paymentmethod */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type PaymentRequest } from '@stripe/stripe-js';
import { logger as LOG, useNoticeBannersStackContext } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { getEnvVars } from '@order/utils';

import { ApplePayCheckoutButton } from '../components';
import { APPLE_PAY_REQUEST } from '../utils';

const logger = LOG.extend('ApplePay');

LOG.enable('ApplePay');

/**
 * This will be replaced with a shared hosted frame as soon as HTTPS+iFrame is working.
 */

export const ApplePay = (props: ApplePayButtonProps) => {
  const stripePromise = useMemo(async () => {
    return loadStripe(getEnvVars().STRIPE_PUBLIC_KEY as string);
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <ApplePayComponent {...props} />
    </Elements>
  );
};

const ApplePayComponent = (props: ApplePayButtonProps) => {
  const {
    amountToCapture,
    isLoading,
    validateCanPlaceOrder,
    onCapturePaymentMethodId,
  } = props;

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );

  const handleApplePayError = useApplePayErrorHandling();

  const handleOnPress = useCallback(() => {
    if (validateCanPlaceOrder() && paymentRequest) {
      paymentRequest.show();
    }
  }, [paymentRequest, validateCanPlaceOrder]);

  const initializeStripe = useCallback(
    async (stripe: Stripe) => {
      const paymentRequestResponse = stripe.paymentRequest({
        ...APPLE_PAY_REQUEST,
        total: { label: APPLE_PAY_REQUEST.label, amount: amountToCapture },
      });

      await paymentRequestResponse.canMakePayment().then((requestResult) => {
        if (!requestResult?.applePay) {
          return;
        }

        setPaymentRequest(paymentRequestResponse);

        paymentRequestResponse.on('cancel', () => {
          handleApplePayError?.({});
        });

        paymentRequestResponse.on('paymentmethod', async (paymentResult) => {
          const paymentMethodId = paymentResult?.paymentMethod?.id;

          logger.info('Apple pay result', paymentMethodId);
          paymentResult.complete('success');

          await onCapturePaymentMethodId?.(paymentMethodId);
        });
      });
    },
    [amountToCapture, handleApplePayError, onCapturePaymentMethodId],
  );

  useStripeInitialization(initializeStripe);

  // Apple pay is either loading or not supported/available.
  if (!paymentRequest) {
    return null;
  }

  return (
    <ApplePayCheckoutButton isLoading={isLoading} onPress={handleOnPress} />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function useStripeInitialization(onStripeInit: (stripe: Stripe) => void) {
  const stripe = useStripe();

  useEffect(() => {
    if (stripe) {
      onStripeInit(stripe);
    }
  }, [stripe, onStripeInit]);
}

const useApplePayErrorHandling = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  return useCallback(
    (error: Readonly<{ message?: string }>) => {
      logger.error(error);

      addNoticeBanner({
        text: error?.message ?? t('general.apple-pay-error'),
        palette: 'caution',
      });
    },
    [t, addNoticeBanner],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Stripe = NonNullable<ReturnType<typeof useStripe>>;
type ApplePayButtonProps = Readonly<{
  amountToCapture: number;
  isLoading: boolean;
  validateCanPlaceOrder: () => boolean;
  onCapturePaymentMethodId?: (paymentMethodId: string) => Promise<void>;
}>;
